import DownloadIcon from '@mui/icons-material/Download';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Breadcrumbs, Button, Container, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadPopup from '/@/components/bayer/download-popup';
import ChipLabel from '/@/components/chip-label';
import Icon from '/@/components/icon';
import Spinner from '/@/components/spinner';
import { useUnitProvider } from '/@/core/context/unit';
import { type Breadcrumb, useBreadcrumbs } from '/@/hooks/breadcrumbs';
import { useCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useCropMonitoringNotes } from '/@/hooks/crop-monitoring-notes';
import { useFieldById } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useReports } from '/@/hooks/reports';
import { useGetHistoricalWeather } from '/@/hooks/weather';
import { _ } from '/@/locale';
import { generateReportPDF } from '/@/store/crop-monitoring/apiProviders';
import { getAppInstance, getSessionLanguage } from '/@/store/session/selectors';
import type { Brand } from '/@/types/catalog';
import type {
    CropMonitoring,
    CropMonitoringApplicationReports,
    CropMonitoringResultNotes,
} from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import type { AppInstance, Units } from '../../../../types/core';
import CropMonitoringReportApplications from './components/applications';
import ReportFooter from './components/footer';
import ReportHeaderData from './components/header';
import ReportMetrics from './components/metrics';
import CropMonitoringReportNotes from './components/notes';
import CropMonitoringReportRecommendations from './components/recommendations';
import PrecipitationReportGraphic from './components/report-graphics/precipitation';
import TemperaturesReportGraphic from './components/report-graphics/temperatures';

export const FieldMonitoringReportContent = ({
    cropMonitoring,
    historicalWeather,
    field,
    cropMonitoringResultNotesQuery,
    applicationReportsQuery,
    appInstance,
    isPdf = false,
    unitsProps = undefined,
    breadcrumbs = [],
    logo,
}: {
    cropMonitoring: CropMonitoring;
    historicalWeather: HistoricalWeather;
    field: Field;
    cropMonitoringResultNotesQuery: CropMonitoringResultNotes;
    applicationReportsQuery: CropMonitoringApplicationReports;
    appInstance: AppInstance;
    isPdf: boolean;
    unitsProps?: {
        brands: Brand[];
        language: string;
        units: Units;
    };
    breadcrumbs: Breadcrumb[];
    logo?: string;
    breadcrumbs: Breadcrumb[];
}) => {
    const language = useSelector(getSessionLanguage);
    const [downloadPDF, setDownloadPDF] = useState(false);

    const plantingYear = new Date(cropMonitoring?.plantingDate).getFullYear();
    const harvestYear = cropMonitoring?.harvestDate ? new Date(cropMonitoring.harvestDate).getFullYear() : plantingYear;

    const hideRecommendedOptions = !cropMonitoring?.active;
    const units = useUnitProvider(unitsProps);

    return (
        <Container sx={{ mt: 4 }}>
            <Stack direction='column' spacing={3} pb={5}>
                {!isPdf && (
                    <Breadcrumbs
                        separator={<NavigateNextIcon color='primary' sx={{ fontSize: '24px !important' }} />}
                        aria-label='breadcrumb'
                    >
                        {breadcrumbs.map((breadcrumb) => (
                            <Typography
                                key={breadcrumb.label}
                                variant='subtitle2'
                                color='primary'
                                onClick={breadcrumb.onClick}
                                sx={{
                                    cursor: breadcrumb.onClick ? 'pointer' : 'default',
                                    fontWeight: !breadcrumb.onClick ? 'bold' : 'normal',
                                }}
                            >
                                {breadcrumb.label}
                            </Typography>
                        ))}
                    </Breadcrumbs>
                )}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent='space-between'
                    alignItems='center'
                >
                    <Stack direction='column' spacing={1}>
                        <Typography variant='h4' fontWeight='semibold' color='primary'>
                            {_('Reporte de cosecha')} {field?.name} {field?.campaign}
                        </Typography>
                        <Typography variant='body2' color='primary.mui.inactive'>
                            {[convertAreaFromM2(field?.area || 0, units).toString(), field?.location].join(' - ')}
                        </Typography>
                        <Stack direction='row' spacing={0.5}>
                            {field?.externalProviderSynced && (
                                <ChipLabel
                                    label={_('Sincronizado con Fieldview')}
                                    type={'fieldview'}
                                    size='sm'
                                    icon={<Icon type={'fieldview'} size='xxs' />}
                                />
                            )}
                            {field?.campaign && (
                                <ChipLabel label={_('Campaña') + ' ' + field?.campaign} type={'campaign'} size='sm' />
                            )}
                        </Stack>
                    </Stack>
                    {isPdf ? (
                        <img src={logo} alt='Logo' style={{ width: 180, height: 40, objectFit: 'contain' }} />
                    ) : (
                        <>
                            <Button
                                variant='contained'
                                color='secondary'
                                startIcon={<DownloadIcon />}
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    whiteSpace: 'nowrap',
                                    minWidth: 'fit-content',
                                }}
                                onClick={() => {
                                    setDownloadPDF(true);
                                }}
                            >
                                {_('Descargar PDF')}
                            </Button>
                            <DownloadPopup
                                visible={downloadPDF}
                                title={_('Descargá tu reporte de cosecha')}
                                onClose={() => setDownloadPDF(false)}
                                onDownloadBefore={async (f) => {
                                    if (f) {
                                        return f;
                                    }
                                    const file = await generateReportPDF({
                                        cropMonitoringId: cropMonitoring?.id,
                                        language,
                                    });
                                    return file;
                                }}
                                onDownloadAfter={() => {
                                    setDownloadPDF(false);
                                }}
                                beforeDownload={_(
                                    'Estamos generando el PDF. Una vez listo, podrás descargarlo en unos segundos.'
                                )}
                                afterDownload={_(
                                    '¡Tu reporte de cosecha ya está listo!\nUna vez descargado, lo vas a encontrar en tu carpeta de Descargas.'
                                )}
                            />
                        </>
                    )}
                </Stack>
                <ReportHeaderData
                    unitsProp={unitsProps}
                    cropMonitoring={cropMonitoring}
                    report={cropMonitoring?.result}
                    field={field}
                    plantingYear={plantingYear}
                    harvestYear={harvestYear}
                />
                <ReportMetrics cropMonitoring={cropMonitoring} unitsProp={unitsProps} isPdf={isPdf} />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap='nowrap'>
                    <Stack sx={{ width: { xs: '100%', md: '50%' } }}>
                        <PrecipitationReportGraphic
                            isPdf={isPdf}
                            unitsProp={unitsProps}
                            cropMonitoring={cropMonitoring}
                            historicalWeather={historicalWeather}
                            width={450}
                        />
                    </Stack>
                    <Stack sx={{ width: { xs: '100%', md: '50%' } }}>
                        <TemperaturesReportGraphic
                            isPdf={isPdf}
                            unitsProp={unitsProps}
                            cropMonitoring={cropMonitoring}
                            historicalWeather={historicalWeather}
                            width={450}
                        />
                    </Stack>
                </Stack>

                <CropMonitoringReportApplications
                    cropMonitoring={cropMonitoring}
                    applicationReportsQuery={applicationReportsQuery}
                    isPdf={isPdf}
                />
                <CropMonitoringReportNotes
                    cropMonitoring={cropMonitoring}
                    cropMonitoringResultNotesQuery={cropMonitoringResultNotesQuery}
                    isPdf={isPdf}
                />
                {!isPdf && !hideRecommendedOptions && (
                    <CropMonitoringReportRecommendations fieldId={field?.id} appInstance={appInstance} />
                )}
                {isPdf && <ReportFooter />}
            </Stack>
        </Container>
    );
};

const FieldMonitoringReport = ({ cropMonitoringId }: { cropMonitoringId: number }) => {
    const cropMonitoringQuery = useCropMonitoringById(cropMonitoringId);
    const historicalWeatherQuery = useGetHistoricalWeather(cropMonitoringId, {
        enabled: Boolean(cropMonitoringId),
    });
    const fieldQuery = useFieldById(
        { id: cropMonitoringQuery.data?.field },
        { enabled: Boolean(cropMonitoringQuery.data?.field) }
    );
    const cropMonitoringResultNotesQuery = useCropMonitoringNotes({
        field: cropMonitoringQuery.data?.field,
        closedCropMonitoring: cropMonitoringQuery.data?.id,
        ordering: '-created',
    });
    const applicationReportsQuery = useReports(
        { fieldId: cropMonitoringQuery.data?.field, type: 'application' },
        { enabled: Boolean(cropMonitoringQuery.data?.field) }
    );
    const appInstance = useSelector(getAppInstance);
    const breadcrumbs = useBreadcrumbs('fieldMonitoring.ReportDetail');

    if (cropMonitoringQuery.isLoading || fieldQuery.isLoading)
        return (
            <Stack height='100%' alignItems='center' justifyContent='center'>
                <Spinner />
            </Stack>
        );
    return (
        <FieldMonitoringReportContent
            cropMonitoring={cropMonitoringQuery.data}
            historicalWeather={historicalWeatherQuery.data}
            field={fieldQuery.data}
            cropMonitoringResultNotesQuery={cropMonitoringResultNotesQuery}
            applicationReportsQuery={applicationReportsQuery}
            appInstance={appInstance}
            isPdf={false}
            breadcrumbs={breadcrumbs}
        />
    );
};

export default FieldMonitoringReport;
